import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const inputBaseClasses = [
  'w-full min-w-0 rounded-md border border-input',
  'bg-transparent text-base shadow-xs transition-[color,box-shadow] outline-none',
  'selection:bg-primary selection:text-primary-foreground placeholder:text-muted-foreground',
  'file:inline-flex file:border-0 file:bg-transparent file:font-medium file:text-foreground',
];

const inputFocusClasses = [
  'focus-visible:border-input focus-visible:ring-[3px] focus-visible:ring-ring/50',
];

const inputStateClasses = [
  'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
  'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
];

const inputVariants = cva([...inputBaseClasses, ...inputFocusClasses, ...inputStateClasses], {
  variants: {
    size: {
      md: 'h-9 px-3 py-1 md:text-sm file:h-7 file:px-2 file:text-sm',
      lg: 'h-12 px-4 py-2 md:text-base file:h-9 file:px-3 file:text-base',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

type InputVariantProps = VariantProps<typeof inputVariants>;

type InputSize = NonNullable<InputVariantProps['size']>;

type InputProps = Omit<React.ComponentProps<'input'>, 'size'> & {
  size?: InputVariantProps['size'];
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, size, ...props }, ref) => {
    return (
      <input
        ref={ref}
        type={type}
        data-slot="input"
        className={cn(inputVariants({ size }), className)}
        {...props}
      />
    );
  },
);

Input.displayName = 'Input';

export { Input };
export type { InputSize };
